"use client";
import styles from "../../guest.module.css";
import registerStyles from "./registerForm.module.css";

import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";

import { Button } from "@/components/atoms";
import Input from "@/components/molecules/NewForm/Input";
import { RegisterStoryblok } from "@/types/storyblok-blok-types";
import { StoryblokStory } from "storyblok-generate-ts";

import { PATHS } from "@/consts";
import Link from "@/lib/bloks/atoms/Link";
import Checkbox from "@/lib/checkout/Checkbox";
import useSignUpUser from "@/lib/hooks/useSelection/account/useRegister";
import { useValidation } from "@/lib/hooks/useValidation";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import NextLink from "next/link";
import { useEffect, useState } from "react";

const defaultValues = {
  email: "",
  password: "",
  repeatPassword: "",
  firstName: "",
  lastName: "",
  // country: "",
  // phoneNumber: "",
  // capSize: ""
};

interface RegisterFormProps {
  pageData: StoryblokStory<RegisterStoryblok>;
}

export const RegisterForm = ({ pageData }: RegisterFormProps) => {
  const { signUp: signUpFunction } = useSignUpUser();
  const t = useTranslations();
  const [loading, setLoading] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [newsletterInvalid, setNewsletterInvalid] = useState(false);

  const errorMessage = signUpFunction.error?.message;

  const validation = useValidation();
  useEffect(() => {
    if (errorMessage) {
      setLoading(false);
    }
  }, [errorMessage]);
  useEffect(() => {
    if (newsletter) {
      setNewsletterInvalid(false);
    }
  }, [newsletter, setNewsletterInvalid]);

  const schema = validation
    .object({
      email: validation.string().email().required(),
      password: validation.string().required(),
      repeatPassword: validation
        .string()
        .oneOf(
          [validation.ref("password")],
          t("common.validation.passwordMatch"),
        )
        .required(),
      firstName: validation.string().required(),
      lastName: validation.string().required(),
      // country: yup.string().required(),
      // phoneNumber: yup.string().required(),
      // capSize: yup.string().required()
    })
    .required();

  const form = useForm({
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { handleSubmit } = form;

  const onSubmit = handleSubmit((formData) => {
    if (!newsletter) {
      setNewsletterInvalid(true);
      return;
    }
    setNewsletterInvalid(false);
    setLoading(true);
    signUpFunction.mutate({
      email: formData.email,
      password: formData.password,
      firstName: formData.firstName,
      lastName: formData.lastName,
      newsletter: newsletter,
      // TODO: more fields will be added later
    });
  });

  const loginLink = pageData.content.logIn?.[0];

  return (
    <div className={clsx(styles.formWrapper, registerStyles.formWrapper)}>
      <FormProvider {...form}>
        <form onSubmit={onSubmit}>
          <Input
            name="email"
            label={t("common.fields.email")}
            inputProps={{ type: "email" }}
          />
          <Input
            name="password"
            label={t("common.fields.password")}
            inputProps={{ type: "password" }}
          />
          <Input
            name="repeatPassword"
            label={t("common.fields.repeatPassword")}
            inputProps={{ type: "password" }}
          />

          {/* TODO: will be added later */}
          {/* TODO: Add select field */}
          {/* <Input name="country" label={t("common.fields.country"} /> */}

          <Input name="firstName" label={t("common.fields.firstName")} />
          <Input name="lastName" label={t("common.fields.lastName")} />

          {/* TODO: will be added later */}
          {/* <Input name="phoneNumber" label={t("common.fields.phoneNumber"} /> */}

          {/* TODO: will be added later */}
          {/* TODO: Add select field */}
          {/* <Input name="capSize" label={t("common.fields.capSize"} /> */}

          {/* TODO: will be added later */}
          {/* <Checkbox
            name="privacyPolicy"
            label={t("myAccount.registerPage.privacyPolicy")}
            inputProps={{ disabled: true }}
          />*/}

          <Checkbox
            label={t.rich("checkout.newsletter", {
              link: (chunks) => (
                <NextLink href={PATHS.privacyPolicy}>{chunks}</NextLink>
              ),
            })}
            onChange={() => setNewsletter(!newsletter)}
            checked={newsletter}
            validate={false}
          />
          {newsletterInvalid && (
            <span className="notification" style={{ margin: "0 0 20px" }}>
              {t.rich("checkout.policyAgreementText", {
                link: (chunks) => (
                  <NextLink href={PATHS.privacyPolicy}>{chunks}</NextLink>
                ),
              })}
            </span>
          )}

          <Button color="black" type="submit" isLoading={loading}>
            {t("myAccount.registerPage.button")}
          </Button>

          {errorMessage && (
            <div
              className="notification"
              dangerouslySetInnerHTML={{ __html: errorMessage }}
            />
          )}

          {loginLink && (
            <div className={styles.linkWrapper}>
              <span>{t("myAccount.registerPage.alreadyMember")}</span>

              <Link blok={loginLink} key={loginLink._uid} />
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
